import { useEffect, useRef } from 'react'

/**
 * Tiny registry of "things that must not be interrupted by an update": dirty forms and active uploads.
 * Forms call `useDirtyForm(id, isDirty, flush)`; upload code calls `markUploadStart/markUploadEnd`
 * (or the `useActiveUpload(active)` hook). The update manager reads it.
 */

type Listener = () => void
export type Flush = () => Promise<boolean> | boolean

interface FormEntry {
  dirty: boolean
  flush?: Flush | undefined
}

const forms = new Map<string, FormEntry>()
let uploads = 0
const listeners = new Set<Listener>()
const emit = () => listeners.forEach((l) => l())

export const activity = {
  setForm(id: string, dirty: boolean, flush?: Flush) {
    const prev = forms.get(id)
    forms.set(id, { dirty, flush })
    if (!prev || prev.dirty !== dirty) emit()
  },
  removeForm(id: string) {
    if (forms.delete(id)) emit()
  },
  uploadStart() {
    uploads += 1
    emit()
  },
  uploadEnd() {
    uploads = Math.max(0, uploads - 1)
    emit()
  },
  hasDirtyForm: () => [...forms.values()].some((f) => f.dirty),
  hasActiveUpload: () => uploads > 0,
  /** What currently blocks an automatic update (uploads take precedence in the message). */
  blocker(): 'upload' | 'form' | null {
    if (uploads > 0) return 'upload'
    return [...forms.values()].some((f) => f.dirty) ? 'form' : null
  },
  /** Writes every dirty form's draft now. Resolves false if any could not be saved on this device. */
  async flushDrafts(): Promise<boolean> {
    const results = await Promise.all(
      [...forms.values()].filter((f) => f.dirty && f.flush).map(async (f) => {
        try {
          return await f.flush!()
        } catch {
          return false
        }
      }),
    )
    return results.every(Boolean)
  },
  subscribe(listener: Listener) {
    listeners.add(listener)
    return () => void listeners.delete(listener)
  },
  /** Test helper. */
  reset() {
    forms.clear()
    uploads = 0
    emit()
  },
}

export const markUploadStart = () => activity.uploadStart()
export const markUploadEnd = () => activity.uploadEnd()

/** Registers a form as dirty while `isDirty`; `flush` writes its draft immediately (called before an update). */
export function useDirtyForm(id: string, isDirty: boolean, flush?: Flush) {
  const flushRef = useRef(flush)
  useEffect(() => {
    flushRef.current = flush
  })
  useEffect(() => {
    activity.setForm(id, isDirty, () => flushRef.current?.() ?? true)
  }, [id, isDirty])
  useEffect(() => () => activity.removeForm(id), [id])
}

/** Counts as an active upload while `active` is true (balanced on unmount). */
export function useActiveUpload(active: boolean) {
  useEffect(() => {
    if (!active) return
    markUploadStart()
    return markUploadEnd
  }, [active])
}
